import {
  NetworkStatus,
  useNetworkStatus,
} from "$global-providers/network-status-provider/provider";
import { useUser } from "$global-providers/user-provider";
import { gql } from "@apollo/client";
import type { SyncableTransaction } from "elephant-web/gql";
import { useCallback, useMemo } from "react";
import { useCashierNamesQuery } from "./__generated__/use-cashier-names.gql";
import type { CashierLabel } from "./payment-history";

// Not named `usersById`: codegen runs the near-operation-file preset over all
// of src/**, and that name is already taken by overview/history/_lib/history.tsx.
gql`
  query CashierNames($ids: [String]!) {
    usersById(ids: $ids) {
      _id
      person {
        firstname
        lastname
      }
    }
  }
`;

const fullName = (
  firstname: string | null | undefined,
  lastname: string | null | undefined,
): string | undefined =>
  [firstname, lastname].filter(Boolean).join(" ") || undefined;

export const useResolveCashierName = (
  transactions: readonly SyncableTransaction[],
): ((userId: string | null | undefined) => CashierLabel | undefined) => {
  const user = useUser();
  const { networkStatus } = useNetworkStatus();

  const ids = useMemo(
    () => [
      ...new Set(
        transactions
          .map((transaction) => transaction.actionedByUserId)
          .filter((id): id is string => Boolean(id) && id !== user.id),
      ),
    ],
    [transactions, user.id],
  );

  // Skipping while offline sends the row straight to `unavailable` rather than
  // parking it on a skeleton until the request times out.
  const { data, loading } = useCashierNamesQuery({
    variables: { ids },
    skip: ids.length === 0 || networkStatus === NetworkStatus.OFFLINE,
    fetchPolicy: "cache-first",
    errorPolicy: "all",
  });

  const nameById = useMemo(() => {
    const names = new Map<string, string>();
    for (const cashier of data?.usersById ?? []) {
      const name =
        cashier &&
        fullName(cashier.person?.firstname, cashier.person?.lastname);
      if (cashier && name) names.set(cashier._id, name);
    }
    return names;
  }, [data]);

  const ownName = fullName(user.firstName, user.lastName);

  return useCallback(
    (userId) => {
      if (!userId) return undefined;

      if (userId === user.id)
        return ownName
          ? { status: "resolved", name: ownName }
          : { status: "unavailable" };

      const name = nameById.get(userId);
      if (name) return { status: "resolved", name };

      return loading ? { status: "loading" } : { status: "unavailable" };
    },
    [loading, nameById, ownName, user.id],
  );
};
